import { DOM } from '@levcharity/resources/scripts/helpers/DOM';
import { validate_password } from '@levcharity/resources/scripts/utils/ValidatePassword';

/**
 * My Account -> Settings for the Leight campaign.
 *
 * The plugin's own handler (MyAccount.editAccount) only binds to a form with the
 * `edit-account-form` class, and it posts as urlencoded, which cannot carry the
 * profile picture. So this page owns its dirty-tracking and submit, and sends
 * multipart FormData to the same `levcharity_my_account_edit` action.
 */
export class MyAccountSettings {
    #container
    #form
    #saveButton
    #messages
    #resetDirtyState

    constructor() {
        this.#container = DOM.el('.my-account-settings-container');

        if (!this.#container) {
            return;
        }

        this.#form = DOM.el('form.woocommerce-EditAccountForm', this.#container);
        this.#saveButton = DOM.el('.save_account_button', this.#container);
        this.#messages = DOM.el('.edit-account-messages', this.#container);

        if (!this.#form || !this.#saveButton) {
            return;
        }

        this.#handleProfileButton();
        this.#handlePasswordFields();
        this.#trackChanges();
        this.#handleSubmit();
    }

    /**
     * The plugin has both of these in MyAccountEdit#initPasswordFields(), but that
     * class' constructor body is commented out, so nothing binds them here. The rules
     * come from the plugin's own validate_password() rather than a copy, so the
     * gamification variant of the checklist (uppercase/lowercase, 8 characters) stays
     * in step with the markup the component renders.
     */
    #handlePasswordFields() {
        DOM.addEvent(
            'click',
            '.form-group .show-hide-password',
            (event, element) => {
                const input = element.closest('.form-group')?.querySelector('input');

                if (!input) {
                    return;
                }

                // Which eye shows is down to the input's type on its own: password.scss
                // hides one or the other through an `input[type="..."] ~` sibling rule.
                input.type = input.type === 'password' ? 'text' : 'password';
            },
            this.#form
        );

        DOM.addEvent(
            'input',
            'input[data-type=password]',
            (event, password) => validate_password(password),
            this.#form
        );
    }

    #handleProfileButton() {
        const wrapper = DOM.el('.profile-picture-wrapper', this.#container);

        if (!wrapper) {
            return;
        }

        const preview = DOM.el('img', wrapper);
        const fileInput = DOM.el('input[type="file"]', wrapper);
        const button = DOM.el('.profile-picture-upload-button', wrapper);

        DOM.addEvent('click', button, (event) => {
            event.preventDefault();
            fileInput?.click();
        });

        DOM.addEvent('change', fileInput, () => {
            const file = fileInput.files?.[0];

            if (!file) {
                return;
            }

            if (preview) {
                const reader = new FileReader();
                reader.onload = (event) => {
                    preview.src = event.target.result;
                };
                reader.readAsDataURL(file);
            }

            this.#saveButton.disabled = false;
        });
    }

    /**
     * Keep Save disabled until something actually changes, then re-arm it after a
     * successful save.
     */
    #trackChanges() {
        const fields = () => this.#form.querySelectorAll('input, select, textarea');
        const initial = new Map();
        let filePicked = false;

        // Radio options in the signup custom fields share a single name, so the value
        // has to be part of the key for them to get one entry each.
        const keyFor = (field) => {
            const key = field.name || field.id;

            if (!key) {
                return '';
            }

            return field.type === 'radio' ? `${key}:${field.value}` : key;
        };

        const valueFor = (field) =>
            field.type === 'checkbox' || field.type === 'radio' ? field.checked : field.value;

        const snapshot = () => {
            initial.clear();
            filePicked = false;
            fields().forEach((field) => {
                const key = keyFor(field);

                if (!key || field.type === 'file') {
                    return;
                }

                initial.set(key, valueFor(field));
            });
        };

        const isDirty = () => {
            if (filePicked) {
                return true;
            }

            let dirty = false;

            fields().forEach((field) => {
                const key = keyFor(field);

                if (!key || field.type === 'file' || !initial.has(key)) {
                    return;
                }

                if (valueFor(field) !== initial.get(key)) {
                    dirty = true;
                }
            });

            return dirty;
        };

        snapshot();
        this.#saveButton.disabled = true;
        this.#resetDirtyState = () => {
            snapshot();
            this.#saveButton.disabled = true;
        };

        const onChange = (event) => {
            // File inputs hold no comparable value, so a pick is recorded as it happens.
            if (event.target?.type === 'file' && event.target.files?.length) {
                filePicked = true;
            }

            this.#saveButton.disabled = !isDirty();
        };

        this.#form.addEventListener('input', onChange);
        this.#form.addEventListener('change', onChange);
    }

    #handleSubmit() {
        // Capture phase so this runs instead of the plugin's no-op click handler.
        this.#saveButton.addEventListener(
            'click',
            (event) => {
                event.preventDefault();
                event.stopImmediatePropagation();

                const errors = this.#validate();

                if (errors.length) {
                    this.#showMessages(errors.map((label) => `${label} is required.`), false);
                    return;
                }

                this.#save();
            },
            true
        );
    }

    #validate() {
        const errors = [];
        const checkedGroups = new Set();

        this.#form.querySelectorAll('[required]').forEach((field) => {
            field.classList.remove('error');

            if (field.type === 'radio' || field.type === 'checkbox') {
                const name = field.name || field.id;

                if (checkedGroups.has(name)) {
                    return;
                }

                checkedGroups.add(name);

                // Radios and checkboxes always report a non-empty value, and one ticked
                // option satisfies a whole radio group, so the group is judged together.
                const group = this.#form.querySelectorAll(`[name="${CSS.escape(name)}"]`);

                if (Array.from(group).some((option) => option.checked)) {
                    return;
                }

                group.forEach((option) => option.classList.add('error'));
                errors.push(this.#labelFor(field));

                return;
            }

            if (field.value && field.value.trim()) {
                return;
            }

            field.classList.add('error');
            errors.push(this.#labelFor(field));
        });

        return errors;
    }

    /**
     * Field headings use `.levcharity_field_label` in this template and
     * `label.group-title` in the plugin's custom-field component.
     */
    #labelFor(field) {
        const label = field
            .closest('.form-group')
            ?.querySelector('.levcharity_field_label:not(.svg-left):not(.svg-right), label.group-title')
            ?.textContent?.replace(/\*/g, '')
            .trim();

        return label || field.placeholder?.replace(/\*/g, '').trim() || field.name;
    }

    #save() {
        const ajaxUrl = (typeof admin_ajax !== 'undefined' && admin_ajax?.[0]) || '/wp-admin/admin-ajax.php';

        this.#saveButton.disabled = true;
        this.#hideMessages();

        // FormData, not urlencoded: the profile picture has to reach $_FILES.
        fetch(ajaxUrl, {
            method: 'POST',
            body: new FormData(this.#form),
            credentials: 'same-origin',
        })
            .then((response) => response.json())
            .then((response) => {
                const messages = response?.messages || response?.message || 'Changes saved.';

                this.#showMessages(messages, Boolean(response?.success));

                if (!response?.success) {
                    this.#saveButton.disabled = false;
                    return;
                }

                this.#resetDirtyState?.();

                // Reload so the saved avatar and values are re-rendered.
                window.setTimeout(() => {
                    window.location = response.redirect_url || window.location.href;
                }, 1000);
            })
            .catch(() => {
                this.#showMessages('An error occurred. Please try again.', false);
                this.#saveButton.disabled = false;
            });
    }

    #showMessages(messages, isSuccess) {
        if (!this.#messages) {
            return;
        }

        const list = Array.isArray(messages) ? messages : [messages];

        this.#messages.innerHTML = list.map((message) => `<div>${message}</div>`).join('');
        this.#messages.classList.toggle('is-success', isSuccess);
        this.#messages.classList.toggle('is-error', !isSuccess);
        this.#messages.hidden = false;
        this.#messages.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    #hideMessages() {
        if (this.#messages) {
            this.#messages.hidden = true;
        }
    }
}
